<template>
<div class="product_detail" @keyup.capture="updateValue()" style="position: relative;">
	<div class="two-columns align-center">
		<div>
			<label-required :label="'UPC' + (productCopy.is_used || productCopy.no_upc ? ' (for item lookup)' : '')" :condition="productCopy.upc" />
			<div v-if="newProduct">
				<input type="text" v-model="productCopy.upc" />
			</div>
			<div v-else class="input-with-control">
				<input type="search" v-model="productCopy.upc" placeholder="Search..." @search="getListProducts()" ref="upc_search" id="upc_sesarch">
				<div class="button action input-go" @click="getListProducts()"><i class="fas fa-search"></i></div>
			</div>
		</div>
		<label v-if="requireUPCSearch && !searchTried">Search for a product by UPC.</label>
		<label v-else-if="!newProduct && !productCopy.product_id">{{ upcSearchMessage }}<div class="inline-link" @click="setNewProduct()">Create a new product?</div></label>
	</div>

	<div class="two-columns">
		<label-required :disabled="disabled" label="Department" :condition="productCopy.department_id" />
		<label-required :disabled="disabled" label="Category" :condition="productCopy.category_id" />
		<select :disabled="disabled" ref="department_id" is-required="1" name="Department ID" v-model="productCopy.department_id" @change="setDepartmentId()">
			<option value="">Select...</option>
			<option v-for="department in departments" :value="department.department_id">{{ department.description }}</option>
		</select>
		<select :disabled="disabled" ref="category_id" is-required="1" name="Category ID" v-model="productCopy.category_id" @change="setCategoryId()">
			<option value="">Select...</option>
			<option v-for="category in categories" :value="category.category_id">{{ category.description }}</option>
		</select>
	</div>

	<div class="two-columns">
		<label>Manufacturer</label>
		<label>{{ productCopy.product_type_code == 'RENTALS' ? 'Serial Number or SKU' : "Manufacturer's SKU" }}</label>
		<div class="input-with-control">
			<input :disabled="disabled" type="text" ref="manufacturer" is-required="1" name="Manufacturer" v-model="productCopy.manufacturer" @change="getManufacturers()">
			<div :class="['button', 'action', 'input-go', { disabled: disabled } ]" @click="getManufacturers()"><i class="fas fa-search"></i></div>
		</div>
		<input :disabled="disabled" type="text" ref="sku" name="SKU" v-model="productCopy.sku">
	</div>

	<div class="two-columns">
		<label>Caliber</label>
		<label>Model</label>
		<div class="input-with-control">
			<input :disabled="disabled" type="text" ref="caliber" is-required="0" name="Caliber" v-model="productCopy.caliber" @change="getCalibers()">
			<div :class="['button', 'action', 'input-go', { disabled: disabled } ]" @click="getCalibers()"><i class="fas fa-search"></i></div>
		</div>
		<input :disabled="disabled" type="text" ref="model" name="Model" v-model="productCopy.model">
	</div>

	<div class="two-one-columns" style="align-items: center;">
		<div>
			<label-required :disabled="disabled" label="Description" :condition="productCopy.description" />
			<input :disabled="disabled" type="text" ref="description" is-required="1" name="Description" v-model="productCopy.description">

			<label>Detailed Description</label>
			<textarea v-model="productCopy.detailed_description" :disabled="disabled" style="height: 4.33em;"/>
		</div>
		<div style="text-align: center;">
			<div v-if="productCopy.image_src">
				<img :src="productCopy.image_src" style="width: auto; max-height: 11em; max-width: 15em;" />
			</div>
			<vue-dropzone v-else-if="newProduct" @vdropzone-success="uploadSuccess" id="image_dropzone" :options="dropzoneOptions"></vue-dropzone>
		</div>
	</div>

	<label-required :disabled="disabled" label="Type" :condition="productCopy.product_type_id" />
	<div class="two-columns">
		<select v-model="productCopy.product_type_id" ref="product_type_id" is-required="1" name="Type" :disabled="disabled" @change="setProductTypeId()">
			<option value="">Select...</option>
			<option v-for="(productType, index) in product_types" :value="productType.product_type_id">{{ productType.description }}</option>
		</select>
		<div v-if="!hideSerializable" class="two-columns" style="padding: 0 1em;">
			<checkbox v-model="productCopy.serializable" label="Serializable" :disabled="disabled" :key="'serializable_' + productCopy.product_id"/>
			<checkbox v-model="productCopy.not_taxable" label="Not Taxable" :disabled="disabled" :key="'not_taxable_' + productCopy.product_id"/>
		</div>
	</div>

	<div v-if="!hideControls" class="controls">
		<div v-if="!noCancel" class="button cancel spaced" @click="$emit('cancel')">Cancel</div>
		<div class="button action spaced" @click="saveProduct()">Save</div>
		<transition name="fade"><div class="message" v-if="message">{{ message }}</div></transition>
	</div>

	<div class="modal" v-if="showModal">
		<div class="inner">
			<h2>{{ $caplitalizeWords(modalForList) }}</h2>
			<div class="content">
				<ul v-if="modalList.length > 0" class="search-list">
					<li class="clickable-list-item" v-for="(listItem, index) in modalList" @click="assignItem(listItem)">{{ listItem.description }}</li>
				</ul>
				<div v-if="modalMessage" class="empty">{{ modalMessage }}</div>
			</div>
			<div class="controls">
				<div v-if="showNewModalInput">
					<label>Create a new {{ modalForList.slice(0,-1) }}</label>
					<input type="text" v-model="newModalInput">
				</div>
				<div class="button cancel spaced" @click="showModal = false">Cancel</div>
				<div v-if="!showNewModalInput" class="button action spaced" @click="showNewModalInput = true">New</div>
				<div v-else class="button action spaced" @click="saveModalInput()">Save</div>
			</div>
		</div>
	</div>

	<div class="modal" v-if="showProductModal">
		<div class="inner">
			<h2>Multiple Products Found</h2>
			<div class="content">
				<ul v-if="modalList.length > 0" class="search-list">
					<li class="clickable-list-item" v-for="(listItem, index) in modalList" @click="assignProduct(index)">
						<div style="display: grid; grid-template-columns: 2em 1fr; grid-column-gap: 1em; align-items: center;">
							<div><img :src="listItem.image_src" style="width: 100%;"/></div>
							<div>
								{{ listItem.description }}<br>
								<label>Mfg</label> {{ listItem.manufacturer }} <label>Model</label> {{ listItem.model }}
							</div>
						</div>
					</li>
				</ul>
				<div v-if="modalMessage" class="empty">{{ modalMessage }}</div>
			</div>
			<div class="controls">
				<div class="button cancel" @click="showProductModal = false">Cancel</div>
			</div>
		</div>
	</div>

</div>
</template>

<script>
import LabelRequired from '@/components/LabelRequired.vue'
import Checkbox from '@/components/Checkbox.vue'
import vue2Dropzone from 'vue2-dropzone'
export default {
	name: 'product-detail-form',
	props: ['product', 'context', 'saveMessage', 'hideSerializable', 'hideControls', 'noCancel', 'requireUPCSearch', 'createProduct'],
    components: {
		'label-required': LabelRequired,
		'checkbox': Checkbox,
		'vue-dropzone': vue2Dropzone
	},
	data() {
		return {
			loading: false,
			client_id: null,
			productCopy: {},
			newProduct: false,
			product_types: [],
			departments: [],
			categories: [],
			manufacturers: [],
			product_types: [],
			message: '',
			showModal: false,
			modalMessage: '',
			modalForList: '',
			modalList: [],
			showNewModalInput: false,
			newModalInput: '',
			searchTried: false,
			searchTerm: '',
			upcSearchMessage: '',
			showProductModal: false,
			uploadProcessing: false
		}
	},
    created() {
		this.productCopy = this.$copy(this.product)
		this.newProduct = this.createProduct || this.product.new_product
		if (this.newProduct) {
			this.searchTried = true
		}
	    this.getDepartments()
		if (this.productCopy.department_id) {
			this.getCategories()
		}
		this.getProductTypes()
	},
    watch: {
		saveMessage(newVal) {
			this.message = newVal
			if (newVal) {
				setTimeout(() => {
					this.message = ''
				}, 2000)
			}
		},
		product(newVal) {
			if (newVal) {
				this.productCopy = this.$copy(this.product)
				if (!this.productCopy.upc) {
					this.$refs.upc_search.focus()
				}
			}
		}
	},
	computed: {
    	changed() {
    		return this.$changed(this.product, this.productCopy)
    	},
    	disabled() {
    		if (!this.newProduct) {
	    		return !this.productCopy.product_id || !this.productCopy.client_id || this.productCopy.client_id != this.client_id
	    	} else if (this.requireUPCSearch && !this.searchTried) {
				return true
			}
	    	return false
    	},
		dropzoneOptions() {
			return {
				url: this.getApiEndpoint + '?action=upload_product_image',
				acceptedFiles: 'image/*',
				withCredentials: true,
				uploadMultiple: false,
				paramName: 'upload_file',
				maxFilesize: 2.0,
				addRemoveLinks: false,
				thumbnailMethod: 'contain',
				dictDefaultMessage: 'Drop an image file here, or click to find an image...',
				params: {
					fd_token: this.$getCookie('dashboard_token'),
					source: 'INVENTORY',
					context: 'PRODUCT',
					product_id: this.productCopy.product_id,
					image_id: this.productCopy.image_id || ''
				}
			}
		}
    },
	methods: {
		updateValue() {
			if (this.hideControls) {
				this.$emit('save', this.productCopy);
			}
		},
		updateFlag() {
			if (this.hideControls) {
				// slight delay before emit allows value of clicked input to propogate
				setTimeout(() =>{
					this.$emit('save', this.productCopy);
				}, 100)
			}
		},
		setNewProduct() {
			this.newProduct = true
			this.productCopy.new_product = true
			this.productCopy.department_id = ''
			this.productCopy.category_id = ''
			this.productCopy.product_type_id = ''
			this.productCopy.product_type_code = ''
			this.updateValue()
		},
		getListProducts() {
			if (this.loading) { return }
			this.upcSearchMessage = ''
			this.searchTerm = this.productCopy.upc
			this.productCopy = {}
			this.updateValue() // reset parent product info
			if (!this.searchTerm) { return }
			this.loading = true
			this.productCopy = {
				upc: this.searchTerm
			}
			this.apiCall({ destination: 'get_item_product_by_upc', data: { upc: this.searchTerm } }).then(obj => {
				this.loading = false
				this.searchTried = true
				if (obj.status == 'success') {
					this.$refs.upc_search.blur()
					if (obj.product_list && obj.product_list.length > 1) {
						this.modalList = obj.product_list
						this.showProductModal = true
					} else {
						this.productCopy = obj.product_detail
						this.productCopy.force_update = true // will tell parent view to re-acquire additional info if necessary
						this.updateValue()
						this.getCategories()
					}
				} else {
					this.upcSearchMessage = obj.message
					// tell parent view to reset all product detail values
					this.productCopy = {
						client_id: this.client_id,
						upc: this.searchTerm
					}
					this.updateValue()
				}
			}).catch(err => {
				console.log(err)
			})
		},
		assignProduct(index) {
			this.productCopy = this.modalList[index]
			this.showProductModal = false
			this.modalList = []
			this.updateValue()
			this.getCategories()
		},
		getProductTypes() {
			this.apiCall({ destination: 'get_list_product_types' }).then(obj => {
				if (obj.status == 'success') {
					this.product_types = obj.product_types
				}
			}).catch(err => {
				console.log(err)
			})
		},
		getDepartments() {
			if (this.departments.length > 0 && this.client_id) { return }
			this.apiCall({ destination: 'get_list_departments', data: { get_all: this.context } }).then(obj => {
				if (obj.status == 'success') {
					this.departments = obj.departments
					this.client_id = obj.client_id
				}
			}).catch(err => {
				console.log(err)
			})
		},
		setDepartmentId() {
			if (this.hideControls) {
				this.$emit('save', this.productCopy)
			}
			this.getCategories()
		},
		getCategories() {
			if (!this.productCopy.department_id) {
				this.categories = []
				return false
			}
			this.apiCall({ destination: 'get_list_categories', data: { department_id: this.productCopy.department_id } }).then(obj => {
				if (obj.status == 'success') {
					this.categories = obj.categories
				}
			}).catch(err => {
				console.log(err)
			})
		},
		setCategoryId() {
			if (this.hideControls) {
				this.$emit('save', this.productCopy)
			}
			this.getGunType()
		},
		getGunType() {
			this.apiCall({ destination: 'get_item_gun_type', data: { department_id: this.productCopy.department_id, category_id: this.productCopy.category_id } }).then(obj => {
				if (obj.status == 'success') {
					this.productCopy.gun_type = obj.gun_type
				}
			}).catch(err => {
				console.log(err)
			})
		},
		setProductTypeId() {
			for (let i = 0; i < this.product_types.length; i++) {
				if (this.product_types[i].product_type_id == this.productCopy.product_type_id) {
					this.productCopy.product_type_code = this.product_types[i].product_type_code
					this.productCopy.serializable = this.product_types[i].is_serializable == 1
					break
				}
			}
			if (this.hideControls) {
				this.$emit('save', this.productCopy)
			}
		},
		getManufacturers() {
			this.modalForList = 'manufacturers'
			this.modalMessage = ''
			this.newModalInput = ''
			this.modalList = []
			this.showNewModalInput = false
			this.apiCall({ destination: 'get_list_manufacturers', data: { search_term: this.productCopy.manufacturer } }).then(obj => {
				if (obj.status == 'success') {
					if (obj.status == 'error') {
						this.modalMessage = obj.message
					} else if (obj.manufacturers.length > 1) {
						this.modalTitle += " like '" + this.productCopy.manufacturer + "'"
						this.modalList = obj.manufacturers
						this.showModal = true
					} else if (obj.manufacturers.length == 1) {
						this.assignItem(obj.manufacturers[0])
					} else {
						this.modalMessage = 'No matching manufacturers found'
						this.newModalInput = this.productCopy.manufacturer
						this.showModal = true
					}
				}
			}).catch(err => {
				console.log(err)
			})
		},
		getCalibers() {
			this.modalForList = 'calibers'
			this.modalMessage = ''
			this.newModalInput = ''
			this.modalList = []
			this.showNewModalInput = false
			this.apiCall({ destination: 'get_list_calibers', data: { search_term: this.productCopy.caliber } }).then(obj => {
				if (obj.status == 'success') {
					if (obj.status == 'error') {
						this.modalMessage = obj.message
					} else if (obj.calibers.length > 1) {
						this.modalTitle += " like '" + this.productCopy.caliber + "'"
						this.modalList = obj.calibers
						this.showModal = true
					} else if (obj.calibers.length == 1) {
						this.assignItem(obj.calibers[0])
					} else {
						this.modalMessage = 'No matching calibers found'
						this.newModalInput = this.productCopy.caliber
						this.showModal = true
					}
				}
			}).catch(err => {
				console.log(err)
			})
		},
		assignItem(listItem) {
			switch(this.modalForList) {
				case 'manufacturers':
					this.productCopy.manufacturer_id = listItem.manufacturer_id
					this.productCopy.manufacturer = listItem.description
					break;
				case 'calibers':
					this.productCopy.caliber_id = listItem.caliber_id
					this.productCopy.caliber = listItem.description
					break;
			}
			this.showModal = false
			this.modalList = []
			this.modalTitle = ''
			this.modalForList = ''
		},
		saveModalInput() {
			if (this.newModalInput.length < 2) { return false }
			switch(this.modalForList) {
				case 'manufacturers':
					this.apiCall({ destination: 'save_item_manufacturer', data: { manufacturer: this.newModalInput } }).then(obj => {
						if (obj.status == 'success') {
							this.productCopy.manufacturer = this.newModalInput
							this.productCopy.manufacturer_id = obj.manufacturer_id
						}
					}).catch(err => {
						console.log(err)
					})
					break;
				case 'calibers':
					this.apiCall({ destination: 'save_item_caliber', data: { caliber: this.newModalInput } }).then(obj => {
						if (obj.status == 'success') {
							this.productCopy.caliber = this.newModalInput
							this.productCopy.caliber_id = obj.caliber_id
						}
					}).catch(err => {
						console.log(err)
					})
					break;
			}
			this.showModal = false
			this.modalList = []
			this.modalTitle = ''
			this.modalForList = ''
		},
		saveProduct() {
			if (!this.productCopy.upc) {
				if (this.productCopy.no_upc || this.productCopy.is_used) {
					this.message = 'Please enter something for a UPC that you can look up later.'
				} else {
					this.message = 'Please enter a UPC.'
				}
				return false
			}
			if (this.productCopy.upc && !this.productCopy.no_upc && this.productCopy.upc.length < 10) {
				this.message = "UPC may not be shorter than 10 digits."
				return false
			}
			for (var property in this.$refs) {
				if (this.$refs[property].getAttribute('is-required') == '1' && !this.productCopy[property]) {
					this.message = this.$refs[property].name + ' may not be blank.'
					return false
				}
			}
			this.$emit('save', this.productCopy)
		},
		uploadSuccess(file, response) {
			var response = JSON.parse(response)
			this.productCopy.image_src = response.image_src
			this.productCopy.image_id = response.image_id
			this.productCopy.new_image = true
			this.updateValue()
			this.$refs['imageDropzone'].removeAllFiles()
		}
	}
}
</script>

<style lang="scss" scoped>
</style>
